import { answerSummary, isAnswered, questionsOf } from "../../shared/questions";
import { reviewTemplate } from "../../shared/review-templates";
import type { ApprovalDTO } from "../../shared/types";
import { AgentAvatar } from "../components/AgentAvatar";
import { Icon } from "../components/Icon";
import { listRowCls } from "../components/List";
import { kindLabel, kindVerb } from "../components/ProposalCard";
import { Badge, timeAgo } from "../components/ui";
import { approvedWord, previewApprovalText, releaseState, staleDays, verdictWord, waitingForText } from "../lib/approval-text";

// One request as a line on the Requests list and as the head of its reader: what it is, where it stands, how long it waited.

const STATUS_TONE: Record<ApprovalDTO["status"], "amber" | "emerald" | "zinc" | "blue"> = {
  pending: "amber", approved: "emerald", rejected: "zinc", answered: "blue", revision_requested: "amber", withdrawn: "zinc",
};

/** Decided by the person it waited for with a word of its own: a question's answer, a note's Add or No. */
const answered = (a: ApprovalDTO) => a.kind === "question" || a.kind === "note";

/** A proposal: decided with the three verdicts on the Requests page's own bar, where the other kinds carry their own card. */
export const isProposal = (a: ApprovalDTO) => a.kind !== "question" && a.kind !== "note" && a.kind !== "review" && a.kind !== "schedule_handover" && a.kind !== "action";

/** Who a request is from, on its line: its agent, or the person who offers a schedule (AIT-62). */
const fromName = (a: ApprovalDTO) => (a.kind === "schedule_handover" ? ((a.payload as { offeredBy?: { name: string } }).offeredBy?.name ?? "Someone") : a.agentName);

/** What the person answered, in the agent's own words where it offered choices; every question of an ask. */
function answerLabel(a: ApprovalDTO): string | null {
  const answers = answerSummary(a);
  if (answers) return answers;
  // An answer from before D14, when an agent could offer choices of its own on a proposal.
  const chosen = a.options.find((o) => o.key === a.decisionKey);
  if (chosen) return chosen.label;
  if (a.kind === "note") return a.status === "approved" ? "Added" : a.status === "rejected" ? "Not added" : null;
  if (a.kind === "question") return a.status === "approved" ? "Yes" : a.status === "rejected" ? "No" : null;
  return null;
}

/** How a decided request reads on its line: who decided it and when, and for an item that sends, whether it went. */
function decidedText(a: ApprovalDTO): string {
  const when = timeAgo(a.decidedAt);
  // Taken back by the agent, or closed by the platform, is decided by nobody.
  if (a.status === "withdrawn") return `withdrawn ${a.decidedByName ? `by ${a.decidedByName} ` : ""}${when}`;
  const label = answerLabel(a);
  const by = `by ${a.decidedByName ?? "someone"} ${when}`;
  const state = releaseState(a);
  const outcome = state === "sent" ? " · sent" : state === "unsent" ? " · not sent" : state === "uncertain" ? ` · may have been sent${a.releaseCheckedAt ? " (checked)" : ""}` : "";
  return label || answered(a) ? `answered "${label ?? verdictWord(a)}" ${by}` : `${verdictWord(a).toLowerCase()} ${by}${outcome}`;
}

export function Meta({ a }: { a: ApprovalDTO }) {
  const questions = questionsOf(a);
  const answeredCount = questions.filter((q) => isAnswered(a.answers?.[q.id])).length;
  // Nobody's run waits on an offered schedule (AIT-62): there is no agent to tell.
  const told = a.kind !== "schedule_handover" && a.status !== "pending" && !(a.status === "withdrawn" && !a.decidedByName);
  return (
    <>
      {a.kind === "question" ? "Asked" : a.kind === "note" ? "Suggested" : a.kind === "review" ? "Filed" : a.kind === "schedule_handover" ? "Offered" : "Proposed"} {timeAgo(a.createdAt)} ·{" "}
      {a.status === "pending"
        ? `waiting for ${waitingForText(a)}${questions.length > 1 ? ` · ${answeredCount} of ${questions.length} answered` : ""}`
        : decidedText(a)}
      {a.decisionNote && ` · "${a.decisionNote}"`}
      {told && (a.answerDeliveredAt ? " · the agent was told" : " · the agent has not been told")}
    </>
  );
}

export function Heading({ a }: { a: ApprovalDTO }) {
  const days = staleDays(a);
  const release = releaseState(a);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-medium">{fromName(a)}</span>
      <span className="text-muted">{kindVerb(a)}{a.kind === "schedule_handover" ? ` ${a.agentName}` : ""}</span>
      <Badge tone={a.kind === "review" || a.kind === "action" ? "amber" : "blue"}>{(a.template && reviewTemplate(a.template)?.label) ?? kindLabel(a)}</Badge>
      {(a.kind === "review" || a.kind === "action") && a.version > 1 && <Badge tone="zinc">version {a.version}</Badge>}
      {a.status !== "pending" && (
        <Badge tone={release === "unsent" || release === "uncertain" ? "amber" : STATUS_TONE[a.status]}>{approvedWord(a).toLowerCase()}</Badge>
      )}
      {days !== null && <Badge tone="amber">Waiting {days} days</Badge>}
    </div>
  );
}

/** One line per request; the full text lives in the viewer. */
export function ApprovalRow({ a, onOpen }: { a: ApprovalDTO; onOpen: () => void }) {
  const preview = a.reason ? previewApprovalText(a.reason) : "";
  return (
    <button type="button" onClick={onOpen} className={`flex w-full min-w-0 items-start gap-3 text-left transition hover:bg-fill-subtle ${listRowCls}`}>
      <AgentAvatar agent={a.agentId} name={a.agentName} />
      <div className="min-w-0 flex-1">
        <Heading a={a} />
        <div className="mt-0.5 truncate text-heading font-semibold tracking-tight">{a.title}</div>
        {preview && <p className="mt-0.5 line-clamp-2 text-sm text-muted">{preview}</p>}
        <div className="mt-1.5 line-clamp-2 text-tiny text-faint">
          <Meta a={a} />
        </div>
        {a.error && <p className="mt-1 text-xs text-danger">Could not apply: {a.error}</p>}
      </div>
      <Icon name="chevron" className="mt-3 size-4 shrink-0 text-faint" />
    </button>
  );
}

/**
 * What needs the viewer first: the stale requests, oldest first, then the rest still waiting, then approved items that
 * did not go out, then the record in the order the server sent it.
 */
export function byUrgency(list: ApprovalDTO[]): ApprovalDTO[] {
  const rank = (a: ApprovalDTO) => (staleDays(a) !== null ? 0 : a.status === "pending" ? 1 : releaseState(a) === "unsent" ? 2 : 3);
  return [...list].sort((x, y) => rank(x) - rank(y) || (rank(x) === 0 ? Date.parse(x.createdAt) - Date.parse(y.createdAt) : 0));
}
